import { MessageMarkdownTransformer } from '@chatwoot/prosemirror-schema';
import { DOMParser, Fragment, Slice } from 'prosemirror-model';
import { replaceVariablesInMessage } from '@chatwoot/utils';
import { createMarkdownInstance } from '../../shared/helpers/MessageFormatter';
import emailSchema from '../components/widgets/WootWriter/emailSchema';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import { ARTICLE_EDITOR_IMAGE_RESIZES } from 'dashboard/constants/editor';
import TurndownService from 'turndown';
import DOMPurify from 'dompurify';

/**
 * Convert markdown content to HTML
 * @param {string} markdown - The markdown content to convert
 * @returns {string} - The HTML representation
 */
export function convertMarkdownToHtml(markdown) {
  if (!markdown) {
    return '';
  }
  return createMarkdownInstance().render(markdown);
}

/**
 * Scrolls the editor view into current cursor position
 *
 * @param {EditorView} view - The Prosemirror EditorView
 *
 */
export const scrollCursorIntoView = view => {
  // Get the current selection's head position (where the cursor is).
  const pos = view.state.selection.head;

  // Get the corresponding DOM node for that position.
  const domAtPos = view.domAtPos(pos);
  const node = domAtPos.node;

  // Scroll the node into view.
  if (node && node.scrollIntoView) {
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

/**
 * Returns a transaction that inserts a node into editor at the given position
 * Has an optional param 'content' to check if the
 *
 * @param {Node} node - The prosemirror node that needs to be inserted into the editor
 * @param {number} from - Position in the editor where the node needs to be inserted
 * @param {number} to - Position in the editor where the node needs to be replaced
 *
 */
export function insertAtCursor(editorView, node, from, to) {
  if (!editorView) {
    return undefined;
  }

  // This is a workaround to prevent inserting content into new line rather than on the exiting line
  // If the node is of type 'doc' and has only one child which is a paragraph,
  // then extract its inline content to be inserted as inline.
  const isWrappedInParagraph =
    node.type.name === 'doc' &&
    node.childCount === 1 &&
    node.firstChild.type.name === 'paragraph';

  if (isWrappedInParagraph) {
    node = node.firstChild.content;
  }

  let tr;
  if (to) {
    tr = editorView.state.tr.replaceWith(from, to, node).insertText(` `);
  } else {
    tr = editorView.state.tr.insert(from, node);
  }
  const state = editorView.state.apply(tr);
  editorView.updateState(state);
  editorView.focus();

  return state;
}

/**
 * Determines the appropriate node and position to insert an image in the editor.
 *
 * Based on the current editor state and the provided image URL, this function finds out the correct node (either
 * a standalone image node or an image wrapped in a paragraph) and its respective position in the editor.
 *
 * 1. If the current node is a paragraph and doesn't contain an image or text, the image is inserted directly into it.
 * 2. If the current node isn't a paragraph or it's a paragraph containing text, the image will be wrapped
 *    in a new paragraph and then inserted.
 * 3. If the current node is a paragraph containing an image, the new image will be inserted directly into it.
 *
 * @param {Object} editorState - The current state of the editor. It provides necessary details like selection, schema, etc.
 * @param {string} fileUrl - The URL of the image to be inserted into the editor.
 * @returns {Object|null} An object containing details about the node to be inserted and its position. It returns null if no image node can be created.
 * @property {Node} node - The ProseMirror node to be inserted (either an image node or a paragraph containing the image).
 * @property {number} pos - The position where the new node should be inserted in the editor.
 */

export const findNodeToInsertImage = (editorState, fileUrl) => {
  const { selection, schema } = editorState;
  const { nodes } = schema;
  const currentNode = selection.$from.node();
  const {
    type: { name: typeName },
    content: { size, content },
  } = currentNode;

  const imageNode = nodes.image.create({ src: fileUrl });

  if (!imageNode) return null;

  const isInParagraph = typeName === 'paragraph';
  const needsNewLine =
    !content.some(n => n.type.name === 'image') && size !== 0 ? 1 : 0;

  return {
    node: isInParagraph ? imageNode : nodes.paragraph.create({}, imageNode),
    pos: selection.from + needsNewLine,
  };
};

/**
 * Resolve a portal's default image size (Portal#default_image_size) into the inline
 * style an article image node carries. The string is built the same way
 * FullEditor.setImageSize builds it, so a freshly inserted image is recognised by
 * detectActiveImageSize and shows up with its preset button already active.
 *
 * @param {string} sizeKey - Preset key, case-insensitive ('small' | 'medium' | 'large' | 'original').
 * @returns {string|null} - The style to set, or null when no size should be applied.
 */
export const articleImageStyleForSize = sizeKey => {
  if (!sizeKey) return null;

  const preset = ARTICLE_EDITOR_IMAGE_RESIZES.find(
    size => size.key === String(sizeKey).toUpperCase()
  );

  return preset?.width ? `width: 100%; max-width: ${preset.width}` : null;
};

/**
 * Set URL with query and size.
 *
 * @param {Object} selectedImageNode - The current selected node.
 * @param {Object} size - The size to set.
 * @param {Object} editorView - The editor view.
 * @param {number} pos - The ProseMirror position of the image node (stored at selection time).
 * @param {boolean} useStyleWidth - When true, sets style="width: X%" instead of height (for HTML/email schema).
 */
export function setURLWithQueryAndSize(
  selectedImageNode,
  size,
  editorView,
  pos,
  useStyleWidth = false
) {
  if (!selectedImageNode) return;

  const nodePos = pos ?? editorView.state.selection.from;
  const attrs = useStyleWidth
    ? {
        src: selectedImageNode.src,
        style: size.width ? `max-width: ${size.width}` : null,
      }
    : { src: selectedImageNode.src, height: size.height };

  const tr = editorView.state.tr.setNodeMarkup(nodePos, null, attrs);
  if (tr.docChanged) {
    editorView.dispatch(tr);
  }
}

/**
 * Content Node Creation Helper Functions for
 * - mention
 * - canned response
 * - variable
 * - emoji
 */

/**
 * Centralized node creation function that handles the creation of different types of nodes based on the specified type.
 * @param {Object} editorView - The editor view instance.
 * @param {string} nodeType - The type of node to create ('mention', 'cannedResponse', 'variable', 'emoji').
 * @param {Object|string} content - The content needed to create the node, which varies based on node type.
 * @returns {Object|null} - The created ProseMirror node or null if the type is not supported.
 */
const createNode = (editorView, nodeType, content) => {
  const { state } = editorView;
  switch (nodeType) {
    case 'mention':
      return state.schema.nodes.mention.create({
        userId: content.id,
        userFullName: content.name,
      });
    case 'cannedResponse':
      // For email schemas, convert markdown to HTML first, then parse with DOM parser
      if (state.schema === emailSchema) {
        const htmlContent = convertMarkdownToHtml(content);
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = DOMPurify.sanitize(htmlContent);
        const parsed = DOMParser.fromSchema(state.schema).parse(tempDiv);
        return parsed.content.size === 1 ? parsed.firstChild : parsed;
      }
      return new MessageMarkdownTransformer(state.schema).parse(content);
    case 'variable':
      return state.schema.text(`{{${content}}}`);
    case 'emoji':
      return state.schema.text(content);
    default:
      return null;
  }
};

/**
 * Object mapping types to their respective node creation functions.
 */
const nodeCreators = {
  mention: (editorView, content, from, to) => ({
    node: createNode(editorView, 'mention', content),
    from,
    to,
  }),
  cannedResponse: (editorView, content, from, to, variables) => {
    const updatedMessage = replaceVariablesInMessage({
      message: content,
      variables,
    });
    const node = createNode(editorView, 'cannedResponse', updatedMessage);
    return {
      node,
      from: node.textContent === updatedMessage ? from : from - 1,
      to,
    };
  },
  variable: (editorView, content, from, to) => ({
    node: createNode(editorView, 'variable', content),
    from,
    to,
  }),
  emoji: (editorView, content, from, to) => ({
    node: createNode(editorView, 'emoji', content),
    from,
    to,
  }),
};

/**
 * Retrieves a content node based on the specified type and content, using a functional approach to select the appropriate node creation function.
 * @param {Object} editorView - The editor view instance.
 * @param {string} type - The type of content node to create ('mention', 'cannedResponse', 'variable', 'emoji').
 * @param {string|Object} content - The content to be transformed into a node.
 * @param {Object} range - An object containing 'from' and 'to' properties indicating the range in the document where the node should be placed.
 * @param {Object} variables - Optional. Variables to replace in the content, used for 'cannedResponse' type.
 * @returns {Object} - An object containing the created node and the updated 'from' and 'to' positions.
 */
export const getContentNode = (
  editorView,
  type,
  content,
  { from, to },
  variables
) => {
  const creator = nodeCreators[type];
  return creator
    ? creator(editorView, content, from, to, variables)
    : { node: null, from, to };
};

const processHtmlSignature = (
  inboxSignature,
  userName,
  userEmail,
  userPosition,
  userCustomLine,
  userEmailSignaturePictureUrl
) => {
  inboxSignature = inboxSignature.replace(/{{name}}/gm, userName);
  inboxSignature = inboxSignature.replace(/{{email}}/gm, userEmail);
  inboxSignature = inboxSignature.replace(/{{position}}/gm, userPosition || '');
  inboxSignature = inboxSignature.replace(
    /{{custom_line}}/gm,
    userCustomLine || ''
  );
  // {{email_signature_picture}} is replaced with the URL of the agent's email signature
  // picture (NOT an <img> tag) — it is meant to be used as the src of an image the agent
  // writes themselves, e.g. <img src="{{email_signature_picture}}">. Empty when no picture
  // is set (no fallback to the profile avatar).
  inboxSignature = inboxSignature.replace(
    /{{email_signature_picture}}/gm,
    userEmailSignaturePictureUrl || ''
  );

  return inboxSignature;
};

// Resolve signature template variables ({{name}}, {{email}}, {{position}}, {{custom_line}},
// {{email_signature_picture}}) using a user object. Used by signature previews so an admin sees
// a real visual (their actual picture URL rendered) while the stored template keeps raw tokens.
export const applySignatureVariables = (html, user = {}) => {
  if (!html) return html;

  return processHtmlSignature(
    html,
    user.available_name || user.name || '',
    user.email || '',
    user.position || '',
    user.custom_line || '',
    user.email_signature_picture_url || ''
  );
};

export const convertHtmlToPlain = html => {
  if (!html) return '';

  const doc = new window.DOMParser().parseFromString(html, 'text/html');

  const blockElements = doc.querySelectorAll(
    'p, div, br, h1, h2, h3, h4, h5, h6, li'
  );
  blockElements.forEach(el => {
    if (el.tagName === 'BR') {
      el.replaceWith('\n');
    } else {
      const textNode = doc.createTextNode('\n');
      el.after(textNode);
    }
  });

  const text = doc.body.textContent || '';

  return text
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .join('\n')
    .trim();
};

/**
 * Convert HTML content to Markdown
 * @param {string} html - The HTML content to convert
 * @returns {string} - The Markdown representation
 */
export const convertHtmlToMarkdown = html => {
  if (!html) return '';

  const turndownService = new TurndownService({
    headingStyle: 'atx',
    codeBlockStyle: 'fenced',
  });

  try {
    return turndownService.turndown(html);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Failed to convert HTML to Markdown:', error);
    // Return original HTML if conversion fails
    return html;
  }
};

/**
 * Removes marks that the editor's toolbar does not support from a pasted slice.
 *
 * The schemas backing the editor (e.g. `messageSchema` used for internal notes)
 * can parse styling marks such as `underline`, `textColor`, `fontSize`, etc.
 * When that styling is pasted it stays in the document and gets serialized back
 * into the message (as raw HTML for the markdown serializer) even though the
 * toolbar offers no way to apply or remove it. This strips any mark whose name
 * is not in `allowedMarks` so pasted content is limited to what the toolbar
 * actually supports. Nodes (headings, lists, …) are left untouched.
 *
 * @param {Slice} slice - The pasted slice produced by ProseMirror.
 * @param {Set<string>|string[]} allowedMarks - Mark names that may be kept.
 * @returns {Slice} A new slice with unsupported marks removed.
 */
export const stripUnsupportedPasteMarks = (slice, allowedMarks) => {
  const allowed =
    allowedMarks instanceof Set ? allowedMarks : new Set(allowedMarks);

  const filterFragment = fragment => {
    const nodes = [];
    fragment.forEach(node => {
      let newNode = node;
      if (node.content && node.content.childCount) {
        newNode = newNode.copy(filterFragment(node.content));
      }
      if (newNode.marks.length) {
        const keptMarks = newNode.marks.filter(mark =>
          allowed.has(mark.type.name)
        );
        if (keptMarks.length !== newNode.marks.length) {
          newNode = newNode.mark(keptMarks);
        }
      }
      nodes.push(newNode);
    });
    return Fragment.fromArray(nodes);
  };

  return new Slice(
    filterFragment(slice.content),
    slice.openStart,
    slice.openEnd
  );
};

const PASTED_LINK_SCHEMES = /^(https?:\/\/|mailto:|tel:)(.+)$/i;
const PASTED_LINK_WWW = /^www\.[^\s.]+\.\S+$/i;

/**
 * Reads the pasted URL out of a clipboard paste, if that is all it holds.
 *
 * Only a single token counts as a URL: anything with whitespace in it is prose
 * (or several links) and should be pasted as usual. Schemes are limited to the
 * ones a link in a message can reasonably use, which also keeps `javascript:`
 * and friends out of an href.
 *
 * @param {string} text - The plain text on the clipboard.
 * @returns {string|null} The href to link with, or null when it is not a URL.
 */
export const getPastedLinkHref = text => {
  const value = (text || '').trim();
  // Whitespace means prose or several links. The rest are characters no pasted
  // URL needs, which the URL parser happens to tolerate inside a host.
  // eslint-disable-next-line no-control-regex
  if (!value || /[\s"<>\u0000-\u001f\u007f]/.test(value)) {
    return null;
  }

  const [, scheme] = value.match(PASTED_LINK_SCHEMES) || [];
  // mailto:/tel: carry no host to validate; http(s) and www. do.
  if (scheme && !scheme.startsWith('http')) {
    return value;
  }
  if (!scheme && !PASTED_LINK_WWW.test(value)) {
    return null;
  }

  const href = scheme ? value : `https://${value}`;
  try {
    // Parses or throws. The parsed value is not used: keeping the pasted text
    // avoids surprising the agent with a normalised (trailing slash, punycode)
    // href.
    // eslint-disable-next-line no-new
    new URL(href);
    return href;
  } catch {
    return null;
  }
};

/**
 * Turns the selected text into a link to the pasted URL, the way Slack and
 * Zendesk do — without this, pasting a URL over a selection replaces the text
 * the agent selected.
 *
 * Any link the selection already carries is dropped first, so pasting over a
 * link re-points it. Only the selected range is touched: pasting over part of a
 * link splits it, unlike the toolbar's link item which always edits the whole
 * link.
 *
 * @param {EditorState} state - The current editor state.
 * @param {string} text - The plain text on the clipboard.
 * @returns {Transaction|null} The transaction to dispatch, or null when the
 * paste should be handled normally.
 */
export const linkSelectionWithPastedUrl = (state, text) => {
  const markType = state.schema.marks.link;
  const { from, to, empty } = state.selection;
  // An empty selection, or a node selection such as a picked image, has no text
  // to carry the link.
  if (!markType || empty || !state.doc.textBetween(from, to, ' ').trim()) {
    return null;
  }

  const href = getPastedLinkHref(text);
  if (!href) {
    return null;
  }

  const tr = state.tr
    .removeMark(from, to, markType)
    .addMark(from, to, markType.create({ href }));

  // Nodes that allow no marks (a code block) drop the mark, leaving a
  // transaction with no steps. Hand the paste back to ProseMirror instead of
  // dispatching a no-op, which would swallow it.
  return tr.docChanged ? tr : null;
};

/**
 * The plain text of a paste. `text/plain` is the source of truth, but Editor.vue
 * swaps the clipboard for an HTML-only one when the user's paste behaviour strips
 * formatting, so the parsed slice is used as the fallback.
 *
 * @param {ClipboardEvent} event - The paste event.
 * @param {Slice} slice - The slice ProseMirror parsed from the clipboard.
 * @returns {string} The pasted text.
 */
export const getPastedText = (event, slice) => {
  const fromClipboard = event?.clipboardData?.getData('text/plain');
  if (fromClipboard) {
    return fromClipboard;
  }
  return slice ? slice.content.textBetween(0, slice.content.size, ' ') : '';
};

/**
 * `handlePaste` for the editors: a URL pasted over selected text links that
 * text instead of replacing it.
 *
 * @param {EditorView} view - The editor the paste landed in.
 * @param {ClipboardEvent} event - The paste event.
 * @param {Slice} slice - The slice ProseMirror parsed from the clipboard.
 * @returns {boolean} true when the paste was turned into a link, false to let
 * ProseMirror paste as usual.
 */
export const handleLinkPaste = (view, event, slice) => {
  const tr = linkSelectionWithPastedUrl(
    view.state,
    getPastedText(event, slice)
  );
  if (!tr) {
    return false;
  }

  view.dispatch(tr);
  return true;
};

/**
 * Finds the additional signature HTML mapped to a given alternative sender email.
 * Tolerates both snake_case (raw inbox getter) and camelCase (getInboxById) keys.
 *
 * @param {Array} additionalHtmlSignatures - [{ html, alternative_emails: [] }]
 * @param {string} email - The alternative sender email to match.
 * @returns {string|null} The mapped signature HTML, or null when unset/blank.
 */
export const findAdditionalSignatureHtml = (
  additionalHtmlSignatures,
  email
) => {
  if (!email || !Array.isArray(additionalHtmlSignatures)) {
    return null;
  }
  const target = email.trim().toLowerCase();
  const match = additionalHtmlSignatures.find(entry => {
    const emails = entry?.alternative_emails ?? entry?.alternativeEmails;
    return (
      Array.isArray(emails) &&
      emails.some(entryEmail => entryEmail?.trim().toLowerCase() === target)
    );
  });
  const html = match?.html;
  return html && html.trim() ? html : null;
};

/**
 * Finds the signature an inbox holds for a country.
 *
 * @param {Object} countrySignatures - { de: html, pl: html, ... }
 * @param {string} country - The conversation's country, as the server sends it.
 * @returns {string|null} The signature HTML, or null when unset/blank.
 */
export const findCountrySignatureHtml = (countrySignatures, country) => {
  const html = country ? countrySignatures?.[country] : null;
  return html && html.trim() ? html : null;
};

/**
 * Resolves the address a reply will be sent from, replicating the backend
 * (Channel::Email#reply_alias_for), so the reply box can pick the signature
 * matching that address:
 *   1. the alias the customer wrote to (latest incoming email to/cc/bcc)
 *   2. none when that email went to the primary address
 *   3. otherwise: the alias used by the most recent outgoing message
 *
 * @param {Array} messages - The conversation's loaded messages.
 * @param {Array} aliasEmails - The addresses of the inbox's aliases.
 * @param {string} primaryEmail - The inbox's primary email address.
 * @returns {string|null} The resolved alternative email, or null.
 */
export const resolveAlternateSenderEmail = (
  messages,
  aliasEmails,
  primaryEmail
) => {
  const aliases = (aliasEmails || []).filter(Boolean);
  if (!aliases.length || !Array.isArray(messages)) {
    return null;
  }
  const lowerAliases = aliases.map(alias => alias.toLowerCase());
  const reversed = [...messages].reverse();

  // The most recent incoming email — the address it was written to decides.
  const lastIncomingEmail = reversed.find(
    message =>
      message?.message_type === MESSAGE_TYPE.INCOMING &&
      message?.content_attributes?.email
  );
  const ca = lastIncomingEmail?.content_attributes || {};
  const recipients = [ca.to_emails, ca.cc_emails, ca.bcc_emails]
    .flat()
    .filter(Boolean)
    .map(recipient => recipient.toLowerCase());

  const recipientIndex = lowerAliases.findIndex(alias =>
    recipients.includes(alias)
  );
  if (recipientIndex !== -1) {
    return aliases[recipientIndex];
  }
  if (primaryEmail && recipients.includes(primaryEmail.toLowerCase())) {
    return null;
  }

  // The most recent outgoing message that carries an email.from
  const lastFrom = reversed
    .filter(message => message?.message_type === MESSAGE_TYPE.OUTGOING)
    .map(message => {
      const from = message?.content_attributes?.email?.from;
      return Array.isArray(from) ? from[0] : from;
    })
    .find(Boolean);

  if (!lastFrom) {
    return null;
  }
  const index = lowerAliases.indexOf(lastFrom.toLowerCase());
  return index === -1 ? null : aliases[index];
};

export const selectSignature = (
  enabled,
  inboxSignature,
  userSignature,
  userName,
  userEmail,
  userPosition,
  userCustomLine,
  userEmailSignaturePictureUrl,
  format = 'html',
  plainSeparator = '\n'
) => {
  if (!enabled) {
    return null;
  }

  const signatureSource =
    (inboxSignature && inboxSignature.trim()) ||
    (userSignature && userSignature.trim());

  if (!signatureSource) {
    return null;
  }

  const signature = processHtmlSignature(
    signatureSource,
    userName,
    userEmail,
    userPosition,
    userCustomLine,
    userEmailSignaturePictureUrl
  );

  if (format === 'plain') {
    return plainSeparator + convertHtmlToPlain(signature);
  }
  return `<div id="html_email_signature" class="html_email_signature border-t border-n-slate-8/20">${signature}</div>`;
};
